import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { HealthResponse, WriteProbeResponse } from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string; code: string };

/**
 * Connection check. The write button exists so the failure path is real and testable:
 * a save is only ever reported as saved when the server returned success (ADR-002).
 */
export function DiagnosticsPage(): JSX.Element {
  const { user, loading } = useAuth();
  const canProbe = Boolean(user);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });

  useEffect(() => {
    const controller = new AbortController();
    apiRequest<HealthResponse>('/api/health', { signal: controller.signal })
      .then((result) => setHealth(result))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setHealthError(error instanceof ApiClientError ? error.message : 'Health check failed.');
      });
    return () => controller.abort();
  }, []);

  const attemptWrite = useCallback(() => {
    if (!user) return;
    setSave({ kind: 'saving' });
    apiRequest<WriteProbeResponse>('/api/diagnostics/write-probe', { method: 'POST' })
      .then(() => setSave({ kind: 'saved' }))
      .catch((error: unknown) => {
        const failure =
          error instanceof ApiClientError
            ? { message: error.message, code: error.code }
            : { message: 'The save failed.', code: 'INTERNAL_ERROR' };
        setSave({ kind: 'failed', ...failure });
      });
  }, [user]);

  return (
    <section data-testid="diagnostics-page">
      <div className="card">
        <h2>API health</h2>
        {health ? (
          <p className="status status--ok" data-testid="health-ok">
            {health.app} {health.version} · {health.environment} ·{' '}
            {health.database.appliedMigrations} migrations applied
          </p>
        ) : null}
        {healthError ? (
          <p className="status status--error" data-testid="health-error">
            {healthError}
          </p>
        ) : null}
        {!health && !healthError ? <p className="meta">Checking…</p> : null}
      </div>

      <div className="card">
        <h2>Write check</h2>
        <p className="meta">
          Attempts a real write. InventoHub never stores a save locally and never reports success
          the server did not confirm.
        </p>
        <button
          type="button"
          className="button"
          onClick={attemptWrite}
          disabled={loading || !canProbe || save.kind === 'saving'}
          data-testid="save-button"
        >
          {save.kind === 'saving' ? 'Saving…' : 'Attempt test write'}
        </button>
        {!loading && !canProbe ? (
          <p className="meta" data-testid="save-signin-required">
            Sign in with Google to run a write check.
          </p>
        ) : null}
        {save.kind === 'saved' ? (
          <p className="status status--ok" data-testid="save-success">
            Saved.
          </p>
        ) : null}
        {save.kind === 'failed' ? (
          <p className="status status--error" data-testid="save-error">
            Not saved — {save.message} ({save.code})
          </p>
        ) : null}
      </div>
    </section>
  );
}
